// ListsPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserCircle, faTrash, faPlus, faLock, faLockOpen, faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import NewListModal from './NewListModal';
import Header from './components/Header';

function ListsPage({ userData, apiClient, onLogout, lists: propLists, onUpdateLists }) {
  const [lists, setLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTrashId, setActiveTrashId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const scrollContainerRef = useRef(null);
  const navigate = useNavigate();
  const IMAGE_BASE_URL = '/img/cocktails/';

  useEffect(() => {
    const fetchLists = async () => {
      try {
        const data = await apiClient.request(`/api/lists?userid=${userData.userid}`, {
          method: 'GET',
        });
        setLists(data || []);
        onUpdateLists(data || []); // Update App.js lists on initial fetch
      } catch (err) {
        setError(err.message || 'Failed to fetch lists');
        if (err.message === 'Failed to refresh token') {
          onLogout();
          navigate('/');
        }
      } finally {
        setLoading(false);
      }
    };

    if (userData && userData.userid && userData.access_token) {
      fetchLists();
    } else {
      setError('User data is incomplete');
      setLoading(false);
    }
  }, [userData, apiClient, onLogout, navigate, onUpdateLists]);

  useEffect(() => {
    if (propLists && propLists.length > 0) {
      setLists(propLists); // Sync local lists with propLists
    }
  }, [propLists]);

  const handleTrashClick = async (listid) => {
    if (listid === 0) {
      setError('Cannot delete Favorites list');
      return;
    }
    if (activeTrashId === listid) {
      try {
        await apiClient.request(`/api/lists/del/${listid}`, {
          method: 'DELETE',
        });
        const updatedLists = lists.filter((list) => list.listid !== listid);
        setLists(updatedLists);
        onUpdateLists(updatedLists); // Update App.js lists after deletion
        setActiveTrashId(null);
      } catch (err) {
        setError(err.message || 'Failed to delete list');
        if (err.message === 'Failed to refresh token') {
          onLogout();
          navigate('/');
        }
      }
    } else {
      setActiveTrashId(listid);
    }
  };

  const handleContainerClick = (e) => {
    if (activeTrashId !== null && !e.target.closest('.action-btn')) {
      setActiveTrashId(null);
    }
  };

  const handleCreateList = (newList) => {
    const updatedLists = [...lists, { ...newList, cocktails: 0, pictures: [] }];
    setLists(updatedLists);
    onUpdateLists(updatedLists); // Update App.js lists after creation
    setIsModalOpen(false);
  };

  const handleListClick = (listid) => {
    navigate(`/cocktails/${listid}`);
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">Loading...</div>;
  }

  if (error) {
    return <div className="text-red-500 text-center">{error}</div>;
  }

  return (
    <div className="lists-container" onClick={handleContainerClick}>
      <div className="lists-gradient-bg"></div>
      <div className="lists-content-container" ref={scrollContainerRef}>
        <Header
          showBack={true}
          title="My Lists"
          subtitle="Organize your favorites"
          showProfile={true}
          onLogout={onLogout}
          onBack={() => navigate(-1)}
        />

        <div className="collections-section">
          <div className="section-sub-header">
            <h2>My Collections</h2>
            <button className="new-list-btn" onClick={() => setIsModalOpen(true)}>
              <FontAwesomeIcon icon={faPlus} className="mr-1" /> New List
            </button>
          </div>

          {lists.map((list) => (
            <div key={list.listid} className="list-card" onClick={() => handleListClick(list.listid)}>
              <div className="list-images">
                <div className="image-grid">
                  {list.pictures && list.pictures.length > 0 && list.pictures.filter((img) => img && img !== 'null').length > 0
                    ? list.pictures
                        .filter((img) => img && img !== 'null')
                        .slice(0, 3)
                        .map((img, idx) => (
                          <img key={idx} src={`${IMAGE_BASE_URL}${img}`} alt={`Cocktail ${idx + 1}`} />
                        ))
                    : null}
                </div>
                <div className="image-overlay"></div>
                <div className="list-info">
                  <h3>{list.listname || 'Unnamed List'}</h3>
                  <p>{list.cocktails || 0} cocktails</p>
                </div>
              </div>
              <div className="list-actions">
                <div className="visibility">
                  <FontAwesomeIcon icon={list.public ? faLockOpen : faLock} className="mr-1" />
                  {list.public ? 'Public' : 'Private'}
                </div>
                {list.listid !== 0 && (
                  <button
                    className={`action-btn ${activeTrashId === list.listid ? 'active' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleTrashClick(list.listid);
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                  </button>
                )}
              </div>
            </div>
          ))}

          {lists.length === 0 && !error && <p className="no-lists">No lists found</p>}
          {error && <p className="error-message">{error}</p>}
        </div>

        <div className="recently-added-section">
          <div className="section-header">
            <h2>Recently Added</h2>
            <a href="#" className="see-all">
              See all
            </a>
          </div>
          <div className="recently-added-list">
            <div className="recent-card">
              <img
                src="https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?..."
                alt="Espresso Martini"
                className="recent-img"
              />
              <div className="recent-content">
                <div className="recent-header">
                  <h3>Espresso Martini</h3>
                  <span>2d ago</span>
                </div>
                <p>Added to Favorites</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <NewListModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onCreate={handleCreateList}
        userData={userData}
        apiClient={apiClient}
        scrollContainerRef={scrollContainerRef}
      />
    </div>
  );
}

export default ListsPage;